import React, { useEffect, useRef, useState } from 'react';
import { Header } from '../../components/Header';
import { SEO } from '../../components/SEO';
import { ClickAnimation } from '../../components/ClickAnimation';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { gsap } from 'gsap';
import { getImageUrl } from '../../lib/imageUrls';

// Extend Window interface for 3D animation properties
declare global {
  interface Window {
    createAnimation?: () => void;
    scene?: THREE.Scene;
    renderer?: THREE.WebGLRenderer;
  }
}

const Home3D: React.FC = () => {
  const sketchRef = useRef<HTMLDivElement>(null);
  const loaderRef = useRef<HTMLDivElement>(null);
  const [hoveredProject, setHoveredProject] = useState<string>('');
  const [isMobile, setIsMobile] = useState(false);
  const [clickAnimation, setClickAnimation] = useState<{ visible: boolean; position: { x: number; y: number } }>({
    visible: false,
    position: { x: 0, y: 0 }
  });
  const navigate = useNavigate();

  // AI Playground thumbnail from S3 (used in ring + mapping so path stays in sync)
  const aiPlaygroundThumbPath = getImageUrl('/_images/3d-homepage/image-thumb-ai.png');

  // Image path to project mapping (exact same as homepage) - using local paths for 3D homepage
  const imageToProjectMapping: {[key: string]: {name: string, link?: string, locked?: boolean}} = {
    // 3D-viewer images (used in homepage)
    [aiPlaygroundThumbPath]: { name: "AI Playground", link: "/ai-playground" },
    "/projects/3D-viewer/img16.png": { name: "Volvo Trucks AR", link: "/volvo-trucks-ar" },
    "/projects/3D-viewer/img17.png": { name: "Nike Tech Pack", link: "/nike" },
    "/projects/3D-viewer/img18.png": { name: "Volvo Trucks Aero", link: "/volvo-trucks-aero" },
    "/projects/3D-viewer/img21.png": { name: "Dieter Rams", link: "/dieter-rams" },
    "/projects/3D-viewer/img15.png": { name: "AI Playground", link: "/ai-playground" },
    "/projects/3D-viewer/img22.png": { name: "Mack Trucks AR", locked: true },
    "/projects/3D-viewer/img24.png": { name: "Nissan IDx", link: "/nissan-idx" },
    "/projects/3D-viewer/img25.png": { name: "Volvo Trucks NA", link: "/volvo-trucks-na" },
    "/projects/3D-viewer/img9.png": { name: "Other", locked: true },
    
    // 3d-homepage images (used in homepage) - mapped to 3D-viewer equivalents
    "/projects/3D-viewer/img4.png": { name: "Mack Trucks", link: "/mack-trucks" },
    "/projects/3D-viewer/img8.png": { name: "Zeekr", link: "/zeekr" },
    "/projects/3D-viewer/img1.png": { name: "Volvo Cars", link: "/volvo-configurator" },
    "/projects/3D-viewer/img3.png": { name: "Formula 1", link: "/formula1" },
    "/projects/3D-viewer/img7.png": { name: "Nivea", link: "/nivea" },
    "/projects/3D-viewer/img11.png": { name: "H&M", locked: true },
    "/projects/3D-viewer/img2.png": { name: "Volvo Rider", link: "/volvo-rider" },
    "/projects/3D-viewer/img12.png": { name: "Lamborghini", locked: true }
  };

  // Handle click animation
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      // Only trigger animation for left mouse button clicks
      if (event.button !== 0) return;
      
      // Prevent conflicts with existing click handlers by using a small delay
      requestAnimationFrame(() => {
        setClickAnimation({
          visible: true,
          position: { x: event.clientX, y: event.clientY }
        });

        // Hide animation after it completes (1.08 seconds based on 27 frames at 25fps)
        setTimeout(() => {
          setClickAnimation(prev => ({ ...prev, visible: false }));
        }, 1080);
      });
    };

    // Use capture phase to ensure we catch clicks before other handlers
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  // Detect mobile screen size and recalibrate on resize
  useEffect(() => {
    const checkMobile = () => {
      const wasMobile = isMobile;
      const nowMobile = window.innerWidth < 768; // Tailwind's md breakpoint
      setIsMobile(nowMobile);
      
      // If mobile state changed, trigger recalibration
      if (wasMobile !== nowMobile) {
        // Small delay to ensure state update is processed
        setTimeout(() => {
          if (window.createAnimation && window.scene && window.renderer) {
            // Dispose current scene
            window.scene.clear();
            window.renderer.dispose();
            
            // Recreate the animation with new mobile state
            window.createAnimation();
          }
        }, 100);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, [isMobile]);

  useEffect(() => {
    console.log('Home3D: Starting reliable 3D animation');
    
    if (!sketchRef.current) return;

    // Cleanup flag to prevent memory leaks
    let isCleanedUp = false;

    // Store event handlers for proper cleanup
    let wheelHandler: ((event: WheelEvent) => void) | null = null;
    let mousedownHandler: ((event: MouseEvent) => void) | null = null;
    let mousemoveHandler: ((event: MouseEvent) => void) | null = null;
    let mouseupHandler: (() => void) | null = null;
    let touchstartHandler: ((event: TouchEvent) => void) | null = null;
    let touchmoveHandler: ((event: TouchEvent) => void) | null = null;
    let touchendHandler: (() => void) | null = null;
    let handleMouseMove: ((event: MouseEvent) => void) | null = null;
    let handleClick: ((event: MouseEvent) => void) | null = null;

    // Create THREE.js scene
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    
    // Store references on window for resize recalibration
    window.scene = scene;
    window.renderer = renderer;
    
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0xffffff, 1);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    
    // Fix color space issues to prevent grey/washed out images
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    sketchRef.current.appendChild(renderer.domElement);

    camera.position.set(0, 0, 16);

    // Handle window resize for camera and renderer
    const handleWindowResize = () => {
      if (isCleanedUp) return;
      
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Update camera aspect ratio
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      
      // Update renderer size
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    };

    // Add resize listener
    window.addEventListener('resize', handleWindowResize);

    // Animation parameters
    const params = {
      transitionProgress: 0,
      enterProgress: 0,
      rotateSpeed: 15
    };

    // Helper functions
    const sumFormula = (n: number) => (n * (n + 1)) / 2;
    const isOdd = (n: number) => n % 2 === 1;

    const circleCount = 2; // Only 2 rings now
    const circleImgCountUnit = 24; // Updated for 24 images

    // Image paths - using local paths for 3D homepage (not S3)
    const logoImages = [
      '/3d-homepage/logo1.png', '/3d-homepage/logo2.png', '/3d-homepage/logo3.png', '/3d-homepage/logo4.png', '/3d-homepage/logo5.png',
      '/3d-homepage/logo6.png', '/3d-homepage/logo7.png', '/3d-homepage/logo8.png', '/3d-homepage/logo9.png', '/3d-homepage/logo10.png',
      '/3d-homepage/logo11.png', '/3d-homepage/logo12.png'
    ];

    const imgImages = [
      // 24 images: img1-img24; slot 13 (index 12) = AI Playground thumbnail (replaces repeat/unmapped slot)
      '/projects/3D-viewer/img1.png', '/projects/3D-viewer/img2.png', '/projects/3D-viewer/img3.png', '/projects/3D-viewer/img4.png', '/projects/3D-viewer/img5.png',
      '/projects/3D-viewer/img6.png', '/projects/3D-viewer/img7.png', '/projects/3D-viewer/img8.png', '/projects/3D-viewer/img9.png', '/projects/3D-viewer/img10.png',
      '/projects/3D-viewer/img11.png', '/projects/3D-viewer/img12.png', aiPlaygroundThumbPath, '/projects/3D-viewer/img14.png', '/projects/3D-viewer/img15.png',
      '/projects/3D-viewer/img16.png', '/projects/3D-viewer/img17.png', '/projects/3D-viewer/img18.png', '/projects/3D-viewer/img19.png', '/projects/3D-viewer/img20.png',
      '/projects/3D-viewer/img21.png', '/projects/3D-viewer/img22.png', '/projects/3D-viewer/img23.png', '/projects/3D-viewer/img24.png'
    ];

    // Load textures with proper sequencing
    const textureLoader = new THREE.TextureLoader();
    const loadedTextures: {[key: string]: THREE.Texture} = {};
    let loadedCount = 0;
    const totalTextures = logoImages.length + imgImages.length;
    let animationStarted = false; // Prevent multiple animation starts

    const startAnimationOnce = () => {
      if (!animationStarted && loadedCount === totalTextures) {
        animationStarted = true;
        console.log('All textures loaded, starting sequential animation');
        createAnimation();
      }
    };

    // Load logo textures sequentially
    logoImages.forEach((path, i) => {
      if (!path || path.trim() === '') {
        console.warn(`Empty path for logo ${i + 1}, skipping`);
        loadedCount++;
        startAnimationOnce();
        return;
      }
      console.log(`Loading logo texture ${i + 1}/${logoImages.length} from: ${path}`);
      textureLoader.load(path, (texture) => {
        // Ensure proper color space for vibrant images
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.flipY = false;
        loadedTextures[`logo${i + 1}`] = texture;
        loadedCount++;
        console.log(`✓ Loaded logo texture ${i + 1}/${logoImages.length}: ${path}`);
        startAnimationOnce();
      }, undefined, (error) => {
        console.error(`✗ Failed to load logo texture ${i + 1}/${logoImages.length} from ${path}:`, error);
        loadedCount++;
        startAnimationOnce();
      });
    });

    // Load img textures sequentially
    imgImages.forEach((path, i) => {
      if (!path || path.trim() === '') {
        console.warn(`Empty path for img ${i + 1}, skipping`);
        loadedCount++;
        startAnimationOnce();
        return;
      }
      console.log(`Loading img texture ${i + 1}/${imgImages.length} from: ${path}`);
      textureLoader.load(path, (texture) => {
        // Ensure proper color space for vibrant images
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.flipY = false;
        loadedTextures[`img${i + 1}`] = texture;
        loadedCount++;
        console.log(`✓ Loaded img texture ${i + 1}/${imgImages.length}: ${path}`);
        startAnimationOnce();
      }, undefined, (error) => {
        console.error(`✗ Failed to load img texture ${i + 1}/${imgImages.length} from ${path}:`, error);
        loadedCount++;
        startAnimationOnce();
      });
    });

    const createAnimation = () => {
      console.log('Creating 3D animation with loaded textures');
      
      // Store createAnimation function on window for resize recalibration
      window.createAnimation = createAnimation;
      
      // Hide loading screen
      document.querySelector(".loader-screen")?.classList.add("hollow");

      const material = new THREE.MeshBasicMaterial({
        transparent: true,
        alphaTest: 0.1
      });

      const r = 6.4 * 0.9 * 1.05; // 10% smaller ring radius + 5% bigger = 5.5% smaller overall
      const scale = isMobile ? 0.4 : 0.8; // 50% smaller on mobile

      // Create rings
      const rings: THREE.Group[] = [];
      const lines: THREE.Group[] = [];
      const allMeshes: Array<{mesh: THREE.Mesh, ringIndex: number, imageIndex: number, totalImages: number}> = [];
      
      for (let i = 0; i < circleCount; i++) {
        const c1 = sumFormula(i) * circleImgCountUnit;
        const c2 = sumFormula(i + 1) * circleImgCountUnit;
        
        const ring = new THREE.Group();
        scene.add(ring);
        rings.push(ring);

        // Get textures based on ring number and mobile state
        const textures: THREE.Texture[] = [];
        if (i === 0) {
          // Ring 1: Use logo images
          if (isMobile) {
            // Mobile: 10 logos evenly spaced
            for (let j = 0; j < 10; j++) {
              if (loadedTextures[`logo${j + 1}`]) {
                textures.push(loadedTextures[`logo${j + 1}`]);
              }
            }
          } else {
            // Desktop: 12 logos
            for (let j = 0; j < 12; j++) {
              if (loadedTextures[`logo${j + 1}`]) {
                textures.push(loadedTextures[`logo${j + 1}`]);
              }
            }
          }
        } else if (i === 1) {
          // Ring 2: Use img images - 24 images for both desktop and mobile
          for (let j = 0; j < 24; j++) {
            if (loadedTextures[`img${j + 1}`]) {
              textures.push(loadedTextures[`img${j + 1}`]);
            }
          }
        }

        textures.forEach((tex, j) => {
          const line = new THREE.Group();
          ring.add(line);
          lines.push(line);
          
          // Proper scaling matching original + mobile optimization
          // Logos (ring 0) are bigger: 20% on desktop, 10% on mobile
          const imgScale = isMobile && i === 0 
            ? 0.008 * scale * (i * 0.36 + 1) * 1.44 * 1.5 * 1.1 // Mobile logos: 10% bigger than original
            : i === 0 
              ? 0.008 * scale * (i * 0.36 + 1) * 1.44 * 1.2 // Desktop logos: 20% bigger than original
              : 0.008 * scale * (i * 0.36 + 1) * 1.44 * 0.25 * 0.8; // Project images: 20% smaller (80% of current 25% size)
          const width = tex.image.width * imgScale;
          const height = tex.image.height * imgScale;
          const geometry = new THREE.PlaneGeometry(width, height);
          const mat = material.clone();
          mat.map = tex;
          mat.needsUpdate = true;
          const mesh = new THREE.Mesh(geometry, mat);
          
          const r2 = isMobile 
            ? (i === 0 ? r * (i + 1) * 0.54 : r * (i + 1) * 0.5) // Mobile: logos 10% smaller (0.6 * 0.9 = 0.54), images 50% smaller
            : r * (i + 1); // Desktop: original size
          // Even spacing around the circle
          const angle = (j / textures.length) * Math.PI * 2; // Perfect even distribution around 360 degrees
          mesh.position.x = r2;
          // Rotate logos 90° on their own axis (ring 0), project images rotated 180° clockwise
          mesh.rotation.z = i === 0 ? -Math.PI / 2 + Math.PI / 2 : -Math.PI / 2 + Math.PI; // Logos rotated 90°, images rotated 180° clockwise
          // Flip logos horizontally to read correctly (ring 0), flip project images horizontally too
          mesh.scale.x = i === 0 ? -1 : -1; // Both logos and project images flipped horizontally
          line.rotation.z = angle;
          line.add(mesh);
          
          // Add hover functionality for images (Ring 2)
          if (i === 1) {
            // All 24 images are hoverable (img1-img24)
            // Use the image path to determine project info
            const imagePath = imgImages[j];
            const projectInfo = imageToProjectMapping[imagePath];
            
            if (projectInfo) {
              // This image has a corresponding project in the homepage list
              mesh.userData = { 
                projectName: projectInfo.name,
                projectLink: projectInfo.link,
                isHoverable: true,
                isClickable: !!projectInfo.link && !projectInfo.locked // Only clickable if there's a link and not locked
              };
            } else {
              // This image doesn't have a corresponding project - no title, no interaction
              mesh.userData = { 
                projectName: '',
                projectLink: undefined,
                isHoverable: false,
                isClickable: false
              };
            }
          }
          
          // Store mesh reference and set initial scale to 0 for animation
          // Preserve horizontal flip for both logos and project images
          mesh.scale.set(-1, 0, 0); // Keep X scale flipped for both logos and project images
          allMeshes.push({
            mesh: mesh,
            ringIndex: i,
            imageIndex: j,
            totalImages: c2 - c1
          });
        });
      }

      // Add solid circle guides
      const dottedCircleMaterial = new THREE.LineBasicMaterial({
        color: 0x0048FF, // Same blue as website indicator
        transparent: true,
        opacity: 0.6,
        linewidth: 3 // Thicker lines
      });

      const dottedCircle1 = new THREE.Group();
      scene.add(dottedCircle1);
      const dottedCircle1Geometry = new THREE.BufferGeometry();
      const dottedCircle1Points = [];
      const segments = 1024;
      for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const circleRadius = isMobile ? 4.712 : 8.568; // 10% bigger on mobile: 4.284 * 1.1 = 4.712
        const x = circleRadius * Math.cos(angle);
        const y = circleRadius * Math.sin(angle);
        dottedCircle1Points.push(new THREE.Vector3(x, y, 0));
      }
      dottedCircle1Geometry.setFromPoints(dottedCircle1Points);
      const dottedCircle1Line = new THREE.Line(dottedCircle1Geometry, dottedCircleMaterial);
      dottedCircle1.add(dottedCircle1Line);

      // Store elements for scroll scaling
      const scrollableElements = [...rings, dottedCircle1];

      // Mobile blue line (3PM position) - only on mobile
      let mobileBlueLine: THREE.Group | null = null;
      if (isMobile) {
        mobileBlueLine = new THREE.Group();
        scene.add(mobileBlueLine);
        
        const blueLineMaterial = new THREE.LineBasicMaterial({
          color: 0x0048FF, // Same blue as website indicator
          transparent: true,
          opacity: 0.8,
          linewidth: 4
        });
        
        const blueLineGeometry = new THREE.BufferGeometry();
        const blueLinePoints = [
          new THREE.Vector3(4, 0, 0), // Start from right edge (50% smaller)
          new THREE.Vector3(8, 0, 0)  // Extend to right (3PM position)
        ];
        blueLineGeometry.setFromPoints(blueLinePoints);
        const blueLine = new THREE.Line(blueLineGeometry, blueLineMaterial);
        mobileBlueLine.add(blueLine);
        
        // Add to scrollable elements
        scrollableElements.push(mobileBlueLine);
      }

      // Top ring - REMOVED (black outer ring)
      // const topRingMaterial = new THREE.LineBasicMaterial({
      //   color: 0x000000,
      //   linewidth: 5,
      //   transparent: true,
      //   opacity: 0.8
      // });

      // const topRing = new THREE.Group();
      // scene.add(topRing);
      // const topRingGeometry = new THREE.BufferGeometry();
      // const topRingPoints = [];
      // for (let i = 0; i <= segments; i++) {
      //   const angle = (i / segments) * Math.PI * 2;
      //   const x = 25.6 * Math.cos(angle);
      //   const y = 25.6 * Math.sin(angle);
      //   topRingPoints.push(new THREE.Vector3(x, y, 0));
      // }
      // topRingGeometry.setFromPoints(topRingPoints);
      // const topRingLine = new THREE.Line(topRingGeometry, topRingMaterial);
      // topRing.add(topRingLine);

      // Progress circle
      const progressCircleMaterial = new THREE.LineBasicMaterial({
        color: 0x000000,
        linewidth: 10,
        transparent: true,
        opacity: 0.9
      });

      const progressCircle = new THREE.Group();
      scene.add(progressCircle);
      progressCircle.position.z = 0.1;
      
      scrollableElements.push(progressCircle);

      // Function to update progress circle based on scroll
      const updateProgressCircle = (progress: number) => {
        progressCircle.remove(...progressCircle.children);
        
        if (progress <= 0) return;
        
        const segmentsToShow = Math.floor(segments * progress);
        if (segmentsToShow <= 0) return;
        
        const progressGeometry = new THREE.BufferGeometry();
        const progressPoints = [];
        
        for (let i = 0; i <= segmentsToShow; i++) {
          const angle = (i / segments) * Math.PI * 2;
          const x = 9.6 * Math.cos(angle);
          const y = 9.6 * Math.sin(angle);
          progressPoints.push(new THREE.Vector3(x, y, 0));
        }
        
        progressGeometry.setFromPoints(progressPoints);
        const progressLine = new THREE.Line(progressGeometry, progressCircleMaterial);
        progressCircle.add(progressLine);
      };

      // Wheel scroller - only prevent default on canvas area
      let scrollTarget = 0;
      let scrollCurrent = 0;
      let scrollDelta = 0;

      const wheelScroller = {
        scroll: {
          target: scrollTarget,
          current: scrollCurrent,
          delta: scrollDelta
        },
        syncScroll: () => {
          // Increase sync speed for more responsive animation
          scrollCurrent += (scrollTarget - scrollCurrent) * 0.2;
          scrollDelta = scrollCurrent;
          // Debug scroll values every 60 frames (roughly once per second)
          if (Math.floor(scrollDelta) % 60 === 0 && scrollDelta > 0) {
            console.log('Scroll sync - Target:', scrollTarget, 'Current:', scrollCurrent, 'Delta:', scrollDelta);
          }
        },
        listenForScroll: () => {
          // Only listen for wheel events on the canvas element
          wheelHandler = (event: WheelEvent) => {
            event.preventDefault();
            console.log('Wheel event detected:', event.deltaY);
            // Increase sensitivity for more responsive scrolling
            scrollTarget += event.deltaY * 2.0;
            scrollTarget = Math.max(0, Math.min(scrollTarget, 1000));
            console.log('Scroll target updated to:', scrollTarget);
          };
          renderer.domElement.addEventListener('wheel', wheelHandler, { passive: false });
        }
      };

      wheelScroller.listenForScroll();

      // Drag detector - only prevent default on canvas area
      let isDragging = false;
      let lastMouseX = 0;
      let lastMouseY = 0;

      const dragDetecter = {
        detectDrag: () => {
          // Only listen for mouse events on the canvas element
          mousedownHandler = (event: MouseEvent) => {
            isDragging = true;
            lastMouseX = event.clientX;
            lastMouseY = event.clientY;
            event.preventDefault();
          };
          renderer.domElement.addEventListener('mousedown', mousedownHandler);

          // Use document for mousemove to track drag outside canvas
          mousemoveHandler = (event: MouseEvent) => {
            if (isDragging) {
              const deltaX = event.clientX - lastMouseX;
              const deltaY = event.clientY - lastMouseY;
              scrollTarget -= (deltaX || deltaY) * 2;
              lastMouseX = event.clientX;
              lastMouseY = event.clientY;
              event.preventDefault();
            }
          };
          document.addEventListener('mousemove', mousemoveHandler);

          mouseupHandler = () => {
            isDragging = false;
          };
          document.addEventListener('mouseup', mouseupHandler);

          // Touch support for mobile - only on canvas
          touchstartHandler = (event: TouchEvent) => {
            if (event.touches.length === 1) {
              isDragging = true;
              lastMouseX = event.touches[0].clientX;
              lastMouseY = event.touches[0].clientY;
              event.preventDefault();
            }
          };
          renderer.domElement.addEventListener('touchstart', touchstartHandler);

          touchmoveHandler = (event: TouchEvent) => {
            if (isDragging && event.touches.length === 1) {
              const deltaX = event.touches[0].clientX - lastMouseX;
              const deltaY = event.touches[0].clientY - lastMouseY;
              scrollTarget -= (deltaX || deltaY) * 2;
              lastMouseX = event.touches[0].clientX;
              lastMouseY = event.touches[0].clientY;
              event.preventDefault();
            }
          };
          document.addEventListener('touchmove', touchmoveHandler);

          touchendHandler = () => {
            isDragging = false;
          };
          document.addEventListener('touchend', touchendHandler);
        }
      };

      dragDetecter.detectDrag();

      // Mouse hover detection for project names
      const raycaster = new THREE.Raycaster();
      const mouse = new THREE.Vector2();

      // Get all meshes for raycasting (defined once for performance)
      const allMeshesForRaycast: THREE.Mesh[] = [];
      rings.forEach(ring => {
        ring.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            allMeshesForRaycast.push(child);
          }
        });
      });

      handleMouseMove = (event: MouseEvent) => {
        // Calculate mouse position in normalized device coordinates
        mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

        // Update the raycaster with camera and mouse position
        raycaster.setFromCamera(mouse, camera);

        // Calculate intersections
        const intersects = raycaster.intersectObjects(allMeshesForRaycast);

        if (intersects.length > 0) {
          const intersectedMesh = intersects[0].object as THREE.Mesh;
          if (intersectedMesh.userData.isHoverable) {
            setHoveredProject(intersectedMesh.userData.projectName);
            
            // Set cursor style based on clickability
            if (intersectedMesh.userData.isClickable) {
              renderer.domElement.style.cursor = 'pointer';
            } else {
              renderer.domElement.style.cursor = 'default';
            }
            
            // Scale up the hovered image
            gsap.to(intersectedMesh.scale, {
              x: -1.2, // Preserve horizontal flip for both logos and project images
              y: 1.2,
              z: 1.2,
              duration: 0.3,
              ease: "power2.out"
            });
            
            // Scale down all other hoverable images
            allMeshesForRaycast.forEach(mesh => {
              if (mesh.userData.isHoverable && mesh !== intersectedMesh) {
                gsap.to(mesh.scale, {
                  x: -1, // Preserve horizontal flip for both logos and project images
                  y: 1,
                  z: 1,
                  duration: 0.3,
                  ease: "power2.out"
                });
              }
            });
          } else {
            setHoveredProject('');
            renderer.domElement.style.cursor = 'default';
            
            // Scale down all hoverable images
            allMeshesForRaycast.forEach(mesh => {
              if (mesh.userData.isHoverable) {
                gsap.to(mesh.scale, {
                  x: -1, // Preserve horizontal flip for both logos and project images
                  y: 1,
                  z: 1,
                  duration: 0.3,
                  ease: "power2.out"
                });
              }
            });
          }
        } else {
          setHoveredProject('');
          renderer.domElement.style.cursor = 'default';
          
          // Scale down all hoverable images
          allMeshesForRaycast.forEach(mesh => {
            if (mesh.userData.isHoverable) {
              gsap.to(mesh.scale, {
                x: -1, // Preserve horizontal flip for both logos and project images
                y: 1,
                z: 1,
                duration: 0.3,
                ease: "power2.out"
              });
            }
          });
        }
      };

      renderer.domElement.addEventListener('mousemove', handleMouseMove);

      // Click handler for navigation
      handleClick = (event: MouseEvent) => {
        // Calculate mouse position in normalized device coordinates
        mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

        // Update the raycaster with camera and mouse position
        raycaster.setFromCamera(mouse, camera);

        // Get all meshes from all rings
        const allMeshesForRaycast: THREE.Mesh[] = [];
        rings.forEach(ring => {
          ring.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              allMeshesForRaycast.push(child);
            }
          });
        });

        // Calculate intersections
        const intersects = raycaster.intersectObjects(allMeshesForRaycast);

        if (intersects.length > 0) {
          const intersectedMesh = intersects[0].object as THREE.Mesh;
          if (intersectedMesh.userData.isClickable && intersectedMesh.userData.projectLink) {
            console.log('Navigating to:', intersectedMesh.userData.projectLink);
            navigate(intersectedMesh.userData.projectLink);
          }
        }
      };

      renderer.domElement.addEventListener('click', handleClick);

      // Mobile title display logic - show titles for images under the blue line
      const updateMobileTitles = () => {
        if (!isMobile) return;
        
        // Find images positioned to the right of center (under the blue line)
        const imagesUnderLine: string[] = [];
        
        rings.forEach(ring => {
          ring.traverse((child) => {
            if (child instanceof THREE.Mesh && child.userData.isHoverable && child.userData.projectName) {
              // Get world position of the mesh
              const worldPosition = new THREE.Vector3();
              child.getWorldPosition(worldPosition);
              
              // Check if image is positioned under the right-aligned blue line (X > 4) and within reasonable Y range
              // Adjusted Y range to be more centered around the blue line
              if (worldPosition.x > 4 && Math.abs(worldPosition.y) < 1.5) {
                imagesUnderLine.push(child.userData.projectName);
              }
            }
          });
        });
        
        // Update hovered project to show the first image under the line
        if (imagesUnderLine.length > 0) {
          setHoveredProject(imagesUnderLine[0]);
        } else {
          setHoveredProject('');
        }
      };

      // Animation loop with cleanup check
      const animate = () => {
        if (isCleanedUp) return; // Stop animation if component is unmounted
        
        requestAnimationFrame(animate);

        wheelScroller.syncScroll();

        // Update rings rotation - reversed direction
        rings.forEach((ring, i) => {
          ring.rotation.z +=
            0.0025 *
            (isOdd(i) ? 1 : -1) * // Reversed rotation direction
            (1 + wheelScroller.scroll.delta) *
            params.rotateSpeed;
        });

        // Apply scroll-based scaling - exact same calculation as original
        const scrollProgress = THREE.MathUtils.mapLinear(
          wheelScroller.scroll.delta,
          0,
          1000,
          0,
          1
        );
        
        // Debug scroll effect every 60 frames
        if (Math.floor(scrollProgress * 60) % 60 === 0 && scrollProgress > 0) {
          console.log('Scroll affecting 3D elements - Progress:', scrollProgress, 'Z position:', -scrollProgress * 100);
        }
        
        scrollableElements.forEach((element) => {
          element.position.z =
            -THREE.MathUtils.lerp(
              0,
              100,
              scrollProgress
            ) + THREE.MathUtils.lerp(10, 0, params.enterProgress);
        });

        // Update progress circle - exact same calculation as original
        updateProgressCircle(scrollProgress);

        // Update image scaling based on scroll progress
        const totalImages = allMeshesForRaycast.length;
        const activeImageIndex = Math.floor(scrollProgress * totalImages);
        
        allMeshesForRaycast.forEach((mesh: THREE.Mesh, index: number) => {
          if (mesh.userData.isHoverable) {
            const isActive = index === activeImageIndex;
            const targetScale = isActive ? 1.1 : 1.0; // Scale active images slightly
            
            gsap.to(mesh.scale, {
              x: -targetScale, // Preserve horizontal flip
              y: targetScale,
              z: targetScale,
              duration: 0.2,
              ease: "power2.out"
            });
          }
        });

        // Update mobile titles if on mobile
        updateMobileTitles();

        renderer.render(scene, camera);
      };

      animate();

      // GSAP animations
      const anime = () => {
        const t1 = gsap.timeline();
        
        t1.to(params, {
          transitionProgress: 1,
          duration: 1,
          ease: "power1.inOut"
        })
        .fromTo(
          params,
          {
            enterProgress: 0,
            rotateSpeed: 10
          },
          {
            enterProgress: 1,
            rotateSpeed: 1,
            duration: 1.5,
            ease: "power1.inOut"
          },
          "-=1"
        )
        .to(
          ".hero-dom",
          {
            opacity: 1
          },
          "-=1"
        );
        
        // Sequential image animations - proper ring-by-ring, then image-by-image sequencing
        const t2 = gsap.timeline();
        
        // Sort meshes by ring index first, then by image index within each ring
        const sortedMeshes = allMeshes.sort((a, b) => {
          if (a.ringIndex !== b.ringIndex) {
            return a.ringIndex - b.ringIndex; // Ring order first
          }
          return a.imageIndex - b.imageIndex; // Then image order within ring
        });
        
        sortedMeshes.forEach(({ mesh, ringIndex, imageIndex }) => {
          // More predictable timing: each ring starts after previous ring completes
          const ringDelay = ringIndex * 0.4; // Increased delay between rings
          const imageDelay = imageIndex * 0.08; // Increased delay between images
          const totalDelay = ringDelay + imageDelay;
          
          console.log(`Animating ring ${ringIndex}, image ${imageIndex} at delay ${totalDelay}s`);
          
          t2.to(mesh.scale, {
            x: -1, // Preserve horizontal flip for both logos and project images
            y: 1,
            z: 1,
            duration: 0.8, // Slightly longer duration for smoother animation
            ease: "back.out(1.7)",
            delay: totalDelay
          }, totalDelay);
        });
      };

      anime();
    };

    // Cleanup function
    return () => {
      // Set cleanup flag to stop animation loop
      isCleanedUp = true;
      
      // Remove event listeners from canvas, document, and window
      if (renderer.domElement) {
        if (wheelHandler) renderer.domElement.removeEventListener('wheel', wheelHandler);
        if (mousedownHandler) renderer.domElement.removeEventListener('mousedown', mousedownHandler);
        if (touchstartHandler) renderer.domElement.removeEventListener('touchstart', touchstartHandler);
        if (handleMouseMove) renderer.domElement.removeEventListener('mousemove', handleMouseMove);
        if (handleClick) renderer.domElement.removeEventListener('click', handleClick);
      }
      
      if (mousemoveHandler) document.removeEventListener('mousemove', mousemoveHandler);
      if (mouseupHandler) document.removeEventListener('mouseup', mouseupHandler);
      if (touchmoveHandler) document.removeEventListener('touchmove', touchmoveHandler);
      if (touchendHandler) document.removeEventListener('touchend', touchendHandler);
      
      // Remove window resize listener
      window.removeEventListener('resize', handleWindowResize);
      
      // Dispose of Three.js resources to prevent memory leaks
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          if (object.geometry) object.geometry.dispose();
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach(material => material.dispose());
            } else {
              object.material.dispose();
            }
          }
        }
      });
      
      // Dispose of textures
      Object.values(loadedTextures).forEach(texture => {
        if (texture && texture.dispose) texture.dispose();
      });
      
      if (sketchRef.current && renderer.domElement) {
        sketchRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
      
      console.log('Home3D: Cleaned up 3D resources and stopped animations');
    };
  }, [isMobile]);

  return (
    <>
      <SEO 
        title="bes.art | 3D Portfolio Experience"
        description="Experience the portfolio of Bes Bujupi through an immersive 3D animation."
        url="https://bes.art/3d"
      />
      <ClickAnimation 
        isVisible={clickAnimation.visible} 
        position={clickAnimation.position} 
      />
      
      {/* 3D Canvas Container */}
      <div id="sketch" ref={sketchRef} className="w-full h-screen bg-white" />
      
      {/* Loading Screen */}
      <div 
        ref={loaderRef}
        className="fixed z-5 top-0 left-0 loader-screen w-screen h-screen transition-all duration-300 bg-white"
      >
        <div className="absolute hv-center">
          <div className="loading-circle">
            <svg width="80" height="80" viewBox="0 0 80 80">
              <circle
                cx="40"
                cy="40"
                r="35"
                fill="none"
                stroke="#0048FF"
                strokeWidth="3"
                strokeDasharray="220"
                strokeDashoffset="220"
                className="loading-circle-fill"
              />
              <circle
                cx="40"
                cy="40"
                r="35"
                fill="none"
                stroke="#0048FF"
                strokeWidth="3"
                strokeOpacity="0.2"
              />
            </svg>
          </div>
        </div>
      </div>
      
      {/* Project Name Display */}
      {hoveredProject && (
        <div className="hero-dom opacity-100">
          <div className={`absolute ${isMobile ? 'bottom-[40px] left-1/2 transform -translate-x-1/2' : 'hv-center'}`}>
            <div 
              className={`${isMobile ? "text-xl cursor-pointer hover:opacity-80 transition-opacity" : "text-4xl"}`}
              style={{color: '#000000', fontFamily: 'ItemsTextTrial-Light, Helvetica', fontStyle: 'normal', lineHeight: 1.2, textAlign: 'center'}}
              onClick={isMobile && hoveredProject ? () => {
                const projectData = imageToProjectMapping[Object.keys(imageToProjectMapping).find(key => 
                  imageToProjectMapping[key].name === hoveredProject
                ) || ''];
                if (projectData && projectData.link && !projectData.locked) {
                  navigate(projectData.link);
                }
              } : undefined}
            >
              {hoveredProject}
            </div>
          </div>
        </div>
      )}

      {/* Header with Navigation */}
      <Header variant="dark" />
    </>
  );
};

export default Home3D;